/**
 * Recent Meals Preview
 * Shows last 3 meal entries for quick view
 */

import { ExternalLink, UtensilsCrossed } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useDayLogs } from '@/hooks/use-nutrition-logs';
import { MEAL_TYPES, STATUS_COLORS } from '@/lib/nutrition';
import type { NutritionLog, MealType } from '@/lib/nutrition';
import { format } from 'date-fns';

interface RecentMealsPreviewProps {
  clientId: string;
  onViewAll?: () => void;
}

function getMealIcon(mealType: MealType): string {
  const meal = MEAL_TYPES.find(m => m.value === mealType);
  return meal?.icon ?? '🍽️';
}

function getMealLabel(mealType: MealType): string {
  const meal = MEAL_TYPES.find(m => m.value === mealType);
  return meal?.label ?? 'Meal';
}

interface MealRowProps {
  log: NutritionLog;
}

function MealRow({ log }: MealRowProps) {
  const statusColor = STATUS_COLORS[log.status];

  return (
    <div className="flex items-center gap-3 py-2">
      {/* Meal icon */}
      <div className="text-lg flex-shrink-0 w-7 text-center">
        {getMealIcon(log.meal_type)}
      </div>

      {/* Meal info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{getMealLabel(log.meal_type)}</span>
          {log.status === 'pending' && (
            <Badge variant="outline" className={cn('text-[10px] h-4 px-1.5', statusColor.bg, statusColor.text)}>
              Pending
            </Badge>
          )}
        </div>
        {log.food_description && (
          <p className="text-xs text-muted-foreground truncate">
            {log.food_description}
          </p>
        )}
      </div>

      {/* Calories */}
      {log.calories && (
        <div className="text-right flex-shrink-0">
          <span className="text-sm font-medium tabular-nums">{log.calories}</span>
          <span className="text-xs text-muted-foreground ml-0.5">cal</span>
        </div>
      )}
    </div>
  );
}

export function RecentMealsPreview({ clientId, onViewAll }: RecentMealsPreviewProps) {
  const { data: logs, isLoading } = useDayLogs(clientId, new Date());

  // Filter out water entries and take first 3 meals
  const mealLogs = (logs || [])
    .filter(log => log.meal_type !== 'water')
    .slice(0, 3);

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Recent Meals</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            <div className="h-10 bg-muted rounded" />
            <div className="h-10 bg-muted rounded" />
            <div className="h-10 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <UtensilsCrossed className="h-4 w-4 text-muted-foreground" />
            Today's Meals
          </CardTitle>
          {logs && logs.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {logs.filter(l => l.meal_type !== 'water').length} entries
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {mealLogs.length === 0 ? (
          <div className="text-center py-6">
            <div className="text-3xl mb-2">🍽️</div>
            <p className="text-sm text-muted-foreground">No meals logged today</p>
            <p className="text-xs text-muted-foreground mt-1">
              Client can log meals in the portal
            </p>
          </div>
        ) : (
          <div className="divide-y">
            {mealLogs.map((log) => (
              <MealRow key={log.id} log={log} />
            ))}
          </div>
        )}

        {onViewAll && logs && logs.length > 3 && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full mt-2 text-xs"
            onClick={onViewAll}
          >
            View all {logs.filter(l => l.meal_type !== 'water').length} entries
            <ExternalLink className="h-3 w-3 ml-1" />
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
